import { createSlice } from "@reduxjs/toolkit";

const defaultMealsPerDay = {
  breakfast: [0],
  lunch: [0],
  dinner: [0],
};

const MEAL_TYPES = ["breakfast", "lunch", "dinner"];

export const getMealRecreationKey = (day, mealType) => `${day}:${mealType}`;

const buildInitialMealToShow = () =>
  [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ].reduce((acc, day) => {
    acc[day] = { ...defaultMealsPerDay };
    return acc;
  }, {});

const initialMealToShow = buildInitialMealToShow();

const initialState = {
  mealToShow: initialMealToShow,
  // Indexes the user unlocked via recreate (10s reveal or API/socket).
  userUnlockedMeals: {},
  // In-flight recreates — survives leaving select-meals (api + countdown).
  // Shape: { "Monday:Breakfast": { day, mealType, mealTypeLower, type, jobId?, previousLength?, endsAt?, hiddenIndex? } }
  pendingRecreations: {},
  mealOptions: {},
};

const selectedMealsSlice = createSlice({
  name: "selectedMeals",
  initialState,
  reducers: {
    setMealsToShow: (state, { payload: { day, mealType, indexes } }) => {
      if (!state.mealToShow[day]) {
        state.mealToShow[day] = { ...defaultMealsPerDay };
      }
      state.mealToShow[day][mealType] = Array.isArray(indexes)
        ? indexes.map(Number).filter((i) => !isNaN(i))
        : [Number(indexes)].filter((i) => !isNaN(i));
    },

    addMealIndex: (state, { payload: { day, mealType, index } }) => {
      if (!state.mealToShow[day]) {
        state.mealToShow[day] = { ...defaultMealsPerDay };
      }

      const currentIndexes = state.mealToShow[day][mealType] || [];
      const numericIndex = Number(index);

      if (!isNaN(numericIndex) && !currentIndexes.includes(numericIndex)) {
        state.mealToShow[day][mealType] = [...currentIndexes, numericIndex];
      }

      if (!isNaN(numericIndex) && numericIndex > 0) {
        if (!state.userUnlockedMeals[day]) {
          state.userUnlockedMeals[day] = {};
        }
        const unlocked = state.userUnlockedMeals[day][mealType] || [];
        if (!unlocked.includes(numericIndex)) {
          state.userUnlockedMeals[day][mealType] = [...unlocked, numericIndex];
        }
      }
    },

    removeMealIndex: (state, { payload: { day, mealType, index } }) => {
      if (state.mealToShow[day]?.[mealType]) {
        state.mealToShow[day][mealType] = state.mealToShow[day][mealType].filter(
          (i) => i !== Number(index),
        );
      }
      if (state.userUnlockedMeals[day]?.[mealType]) {
        state.userUnlockedMeals[day][mealType] = state.userUnlockedMeals[day][
          mealType
        ].filter((i) => i !== Number(index));
      }
    },

    restoreUnlockedMealsToShow: (state) => {
      if (!state.userUnlockedMeals) {
        state.userUnlockedMeals = {};
      }
      if (!state.pendingRecreations) {
        state.pendingRecreations = {};
      }

      state.mealToShow = buildInitialMealToShow();

      Object.entries(state.userUnlockedMeals).forEach(([day, meals]) => {
        if (!state.mealToShow[day]) {
          state.mealToShow[day] = { ...defaultMealsPerDay };
        }
        MEAL_TYPES.forEach((mealType) => {
          const unlocked = meals?.[mealType] || [];
          if (!unlocked.length) return;
          state.mealToShow[day][mealType] = [
            ...new Set([0, ...unlocked.map(Number).filter((i) => !isNaN(i))]),
          ].sort((a, b) => a - b);
        });
      });
    },

    startPendingRecreation: (state, { payload }) => {
      if (!state.pendingRecreations) {
        state.pendingRecreations = {};
      }
      const key = getMealRecreationKey(payload.day, payload.mealType);
      state.pendingRecreations[key] = {
        ...state.pendingRecreations[key],
        ...payload,
      };
    },

    clearPendingRecreation: (state, { payload: { day, mealType } }) => {
      if (!state.pendingRecreations) return;
      const key = getMealRecreationKey(day, mealType);
      delete state.pendingRecreations[key];
    },

    clearAllPendingRecreations: (state) => {
      state.pendingRecreations = {};
    },

    resetAllMealsToShow: (state) => {
      state.mealToShow = buildInitialMealToShow();
      state.userUnlockedMeals = {};
      state.pendingRecreations = {};
    },

    addMealOption: (state, { payload: { day, mealType, mealId } }) => {
      if (!state.mealOptions[day]) {
        state.mealOptions[day] = {};
      }
      if (!state.mealOptions[day][mealType]) {
        state.mealOptions[day][mealType] = [];
      }
      const existingMeals = state.mealOptions[day][mealType];
      if (!existingMeals.includes(mealId)) {
        existingMeals.push(mealId);
      }
    },
  },
});

export const {
  setMealsToShow,
  addMealIndex,
  removeMealIndex,
  restoreUnlockedMealsToShow,
  startPendingRecreation,
  clearPendingRecreation,
  clearAllPendingRecreations,
  resetAllMealsToShow,
  addMealOption,
} = selectedMealsSlice.actions;

export default selectedMealsSlice;
